import type { NextPage } from 'next';
import type { AppContext, AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { Provider, ReactReduxContext } from 'react-redux';
import { load as loadAnalytics } from '@peloton/analytics/analytics';
import { makeAnalytics } from '@members/analytics/analytics';
import { init as initDatadogReporting } from '@members/errors/datadog';
import Page from '@members/layout/Page';
import { GlobalComponents } from '../_components/GlobalComponents';
import GlobalContextProviders from '../_components/GlobalContextProviders';
import GlobalModals from '../_components/GlobalModals';
import { GlobalScripts } from '../_components/GlobalScripts';
import { GlobalStyles } from '../_components/GlobalStyles';
import { CustomHead } from '../_components/Head';
import { ShareMetaTags } from '../_components/ShareMetaTags';
import { env } from '../_lib/env';
import { generateStore } from '../_lib/store';
import '../_lib/a11y';
import { toLambdas } from '../lambdas/toLambdas';
import type { ShareMetaTagType } from '../lambdas/utils/html';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = Partial<AppProps> & {
  Component: NextPageWithLayout;
};

type AppWebProps = {
  shareMetadata?: ShareMetaTagType[];
};

if (typeof window !== 'undefined') {
  initDatadogReporting();
  loadAnalytics({ key: env.segmentWriteKey }, makeAnalytics());
}

const { store, client } = generateStore({
  env,
  errorHandler: { reportError: () => {}, sagaOnError: () => {} },
});

// TODO: Reenable error reporters
export default function AppWeb({
  shareMetadata,
  Component,
  pageProps,
}: AppProps & AppWebProps) {
  return (
    <>
      <CustomHead />
      <GlobalScripts />
      <ShareMetaTags shareMetadata={shareMetadata} />
      <Provider store={store} context={ReactReduxContext}>
        <div id="root">
          <GlobalContextProviders reduxStore={store} reduxClient={client}>
            <GlobalStyles />
            <GlobalComponents />
            <GlobalModals />
            <PageComponent Component={Component} pageProps={pageProps} />
          </GlobalContextProviders>
        </div>
      </Provider>
    </>
  );
}

const PageComponent = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout || (page => page);

  return <Page>{getLayout(<Component {...pageProps} />)}</Page>;
};

AppWeb.getInitialProps = async (context: AppContext): Promise<AppWebProps> => {
  const shareMetadata = await toLambdas(context);

  return { shareMetadata };
};
